<template>
  <div class="tw-p-4 sm:tw-p-6">
    <div class="tw-mb-4 tw-text-left">
      <h3 class="tw-text-lg tw-font-semibold tw-text-slate-900">Account</h3>
      <div class="tw-mt-1 tw-text-sm tw-text-slate-600">Update your name and password.</div>
    </div>

    <div v-if="userLoading" class="tw-py-8 tw-text-center tw-text-sm tw-text-slate-500">
      Loading…
    </div>

    <div v-else class="tw-max-w-2xl tw-space-y-4">
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
        <InputField v-model="form.name" label="Name" autocomplete="name" />
        <div>
          <div class="tw-block tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500">
            Email
          </div>
          <div class="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-600">
            {{ form.email || '—' }}
          </div>
        </div>
        <InputField
          v-model="form.password"
          label="New password"
          autocomplete="new-password"
          placeholder="Leave blank to keep current password"
        />
      </div>

      <div class="tw-mt-5 tw-flex tw-justify-end tw-gap-2">
        <Button
          :isGreen="true"
          icon="check"
          :enableLoading="isSaving"
          :disabled="isSaving"
          @click="saveProfile"
        >
          Save
        </Button>
        <Button :isRed="true" :isSecondary="true" :disabled="isSaving" @click="resetForm">
          Cancel
        </Button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import EditSelfProfileMutation from '@/gql/user/EditSelfProfile.gql';
import CurrentUserQuery from '@/gql/user/user.gql';
import { pushToast } from '@/lib/toast';
import { useAuthStore } from '@/stores/auth';
import Button from '../core/Button.vue';
import InputField from '../core/Input/InputField.vue';

export default defineComponent({
  name: 'ProfileAccountTab',
  components: {
    Button,
    InputField,
  },
  apollo: {
    user: {
      query: CurrentUserQuery,
      loadingKey: 'userLoading',
      update: (data: any) => data?.user,
      result() {
        this.resetForm();
      },
    },
  },
  data() {
    return {
      userLoading: 0,
      isSaving: false,
      form: {
        name: '',
        email: '',
        password: '',
      },
    };
  },
  methods: {
    resetForm() {
      const vm = this as any;
      const user = vm.user;
      vm.form = {
        name: user?.fullName || '',
        email: user?.email || '',
        password: '',
      };
    },
    saveProfile() {
      const vm = this as any;
      if (vm.isSaving) {
        return;
      }
      if (!vm.form.name?.trim()) {
        pushToast('Name is required', 'warning');
        return;
      }

      const variables: Record<string, unknown> = {
        name: vm.form.name.trim(),
      };
      const password = vm.form.password?.trim();
      if (password) {
        variables.password = password;
      }

      vm.isSaving = true;
      vm.$apollo
        .mutate({
          mutation: EditSelfProfileMutation,
          variables,
        })
        .then((result: any) => {
          const updatedName = result?.data?.editSelfProfile?.user?.fullName;
          if (updatedName) {
            useAuthStore().setUsername(updatedName);
          }
          pushToast('Profile updated', 'success');
          vm.form.password = '';
          if (vm.$apollo?.queries?.user) {
            vm.$apollo.queries.user.refetch();
          }
        })
        .catch((error: any) => {
          console.error(error);
          pushToast('Failed to update profile', 'error');
        })
        .finally(() => {
          vm.isSaving = false;
        });
    },
  },
});
</script>
